/**
 * Custom hook for managing the state of an existing room editing session.
 * For new room creation, use useRoomCreate instead.
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { EventTimeline, RoomStateEvent } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import { useContactList } from './useContactList';
import { useAvatar } from '../context/AvatarContext';
import matrixService from '../services/matrixService';
import logger from '../utils/logger';
import { GEMATIK_ROOM } from '../constants';
import {
  canUserEditRoomName,
  canUserEditRoomTopic,
  dualWriteRoomName,
  dualWriteRoomTopic,
} from '../utils/gematikRoomUtils';
import { generateInitialsAvatar } from '../utils/avatar';

export const useRoomEdit = (options = {}) => {
  const {
    existingRoomId = null,
    allowRemoveCurrentUser = false,
    minParticipants = 1,
    maxSelections = null, // null = unlimited, number = max selections (with auto-replace)
    initialAvatarOption = 'default', // Initial avatar selection ('default', preset path, or 'custom')
  } = options;

  const client = useMatrixClient();
  const { contactList: contacts } = useContactList();
  const { getRoomAvatar, getFallbackAvatars, getFallbackAvatarsFromMembers, getUserAvatar } = useAvatar();
  const myUserId = client?.getUserId();
  const currentUser = myUserId ? client?.getUser(myUserId) : null;

  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [editedTopic, setEditedTopic] = useState(''); // New topic state

  // Track users not in contact list (e.g., from user directory search)
  const [externalUsers, setExternalUsers] = useState({}); // userId -> {id, name, avatar}

  // Track room state updates to force permission recalculation
  // This ensures powerLevels memo recalculates when room state changes (e.g., when a user joins)
  const [stateUpdateCounter, setStateUpdateCounter] = useState(0);

  
  // Avatar state
  const [selectedAvatarOption, setSelectedAvatarOption] = useState(initialAvatarOption);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarUrls, setAvatarUrls] = useState([]);
  const [defaultAvatarUrls, setDefaultAvatarUrls] = useState([]); // Always holds member avatars for modal display
  const [originalAvatarOption, setOriginalAvatarOption] = useState(initialAvatarOption); // Track original state for change detection

  // This effect resets and initializes the selection state whenever the room changes.
  // It also listens for room state updates to keep selectedContactIds in sync with actual room membership.
  useEffect(() => {
    if (!existingRoomId || !client) {
      return;
    }

    const room = client.getRoom(existingRoomId);
    if (!room) {
      return;
    }

    // Listener for specific events that affect permissions
    // Only power level changes and current user membership changes should trigger permission recalculation
    const handlePermissionRelevantEvents = (event) => {
      if (!event || event.getRoomId() !== room.roomId) {
        return;
      }

      const eventType = event.getType();
      const isPowerLevelChange = eventType === 'm.room.power_levels';

      // For member events, only trigger on actual membership status changes
      // (not avatar or display name changes)
      let isMyMembershipStatusChange = false;
      if (eventType === 'm.room.member' && event.getStateKey() === myUserId) {
        const content = event.getContent();
        const prevContent = event.getPrevContent();

        // Check if membership field actually changed (invite→join, join→leave, etc.)
        // Ignore avatar_url or displayname changes
        const currentMembership = content?.membership;
        const previousMembership = prevContent?.membership;

        if (currentMembership && currentMembership !== previousMembership) {
          isMyMembershipStatusChange = true;
        }
      }

      if (isPowerLevelChange) {
        logger.debug('[useRoomEdit] Power levels changed, incrementing counter');
        // Increment counter to force powerLevels memo recalculation
        // This ensures permissions update when power levels change or current user's membership status changes
        setStateUpdateCounter(prev => prev + 1);
      }

      if (isMyMembershipStatusChange) {
        logger.debug('[useRoomEdit] My membership changed, incrementing counter');
        setStateUpdateCounter(prev => prev + 1);
      }
    };

    // Listener for participant list updates (all membership changes)
    // This keeps the participant list in sync without triggering permission recalculation
    const handleParticipantListUpdate = (state) => {
      if (state.roomId === room.roomId) {
        const participantIds = room.getMembers()
          .filter(member => member.membership === 'join' || member.membership === 'invite')
          .map(member => member.userId)
          .filter(userId => userId !== myUserId);

        logger.debug('[useRoomEdit] Updating selectedContactIds from room members', {
          roomId: existingRoomId,
          participantIds,
        });

        setSelectedContactIds(participantIds);
      }
    };

    // Initialize with the members of the existing room
    const participantIds = room.getMembers()
      .filter(member => member.membership === 'join' || member.membership === 'invite')
      .map(member => member.userId)
      .filter(userId => userId !== myUserId);

    logger.debug('[useRoomEdit] Initial selectedContactIds', {
      roomId: existingRoomId,
      participantIds,
    });

    setSelectedContactIds(participantIds);

    // Initialize topic from room
    const topicEvent = room.getLiveTimeline()
      .getState(EventTimeline.FORWARDS)
      .getStateEvents(GEMATIK_ROOM.ROOM_TOPIC, '');
    const roomTopic = topicEvent?.getContent()?.topic || '';
    setEditedTopic(roomTopic);

    // Initialize avatar state based on current room avatar
    const roomAvatarMxc = room.getMxcAvatarUrl();
    let initialOption;
    if (roomAvatarMxc) {
      // Room has an explicit avatar set
      // Check if it's a preset by examining the MXC URL or we default to 'custom'
      // Since we can't easily determine if it's a preset from MXC, we'll set to 'custom'
      // User can change to preset if desired
      initialOption = 'custom';
      setSelectedAvatarOption('custom');
    } else {
      // No explicit avatar - use default (member avatars)
      initialOption = 'default';
      setSelectedAvatarOption('default');
    }
    setOriginalAvatarOption(initialOption); // Track original for change detection
    setSelectedAvatarFile(null);

    // Listen for permission-relevant events (power levels + current user membership)
    room.currentState.on(RoomStateEvent.Events, handlePermissionRelevantEvents);

    // Listen for participant list updates (all membership changes)
    room.currentState.on(RoomStateEvent.Update, handleParticipantListUpdate);

    // Listener for room avatar changes to keep avatar state in sync
    const handleAvatarChange = (event) => {
      if (!event || event.getRoomId() !== room.roomId) {
        return;
      }

      const eventType = event.getType();
      if (eventType === 'm.room.avatar') {
        logger.debug('[useRoomEdit] Room avatar changed, updating avatar state');
        const roomAvatarMxc = room.getMxcAvatarUrl();
        let newOption;
        if (roomAvatarMxc) {
          // Room now has an explicit avatar - treat as custom
          newOption = 'custom';
          setSelectedAvatarOption('custom');
        } else {
          // Avatar was removed - use default
          newOption = 'default';
          setSelectedAvatarOption('default');
        }
        setOriginalAvatarOption(newOption);
        setSelectedAvatarFile(null); // Clear any file selection since avatar is now synced with server
      }
    };

    // Listen for room avatar changes
    room.currentState.on(RoomStateEvent.Events, handleAvatarChange);

    // Cleanup listeners when component unmounts or dependencies change
    return () => {
      room.currentState.off(RoomStateEvent.Events, handlePermissionRelevantEvents);
      room.currentState.off(RoomStateEvent.Update, handleParticipantListUpdate);
      room.currentState.off(RoomStateEvent.Events, handleAvatarChange);
      // Clean up external users cache when room changes to prevent memory growth
      setExternalUsers({});
    };
  }, [client, existingRoomId, myUserId]); // Only these dependencies

  // Get the full room object from the SDK
  const room = useMemo(() => {
    if (client && existingRoomId) {
      return client.getRoom(existingRoomId);
    }
    return null;
  }, [client, existingRoomId]);

  // Get the actual contact objects for selected IDs
  // Include both contacts from contact list AND external users (from search)
  const selectedContacts = useMemo(() => {
    const knownContacts = contacts.filter(contact => selectedContactIds.includes(contact.id));
    
    // Add external users (not in contact list)
    const knownContactIds = new Set(knownContacts.map(c => c.id));
    const externalContacts = selectedContactIds
      .filter(id => !knownContactIds.has(id))
      .map(id => externalUsers[id])
      .filter(Boolean);
    
    const allContacts = [...knownContacts, ...externalContacts];
    
    // Enrich with powerLevel from room members if editing an existing room
    if (room) {
      return allContacts.map(contact => {
        const member = room.getMember(contact.id);
        const powerLevel = member?.powerLevel ?? 0;
        return {
          ...contact,
          powerLevel,
        };
      });
    }
    
    return allContacts;
  }, [selectedContactIds, contacts, externalUsers, room]);
  
  // Fetch profile info for selected users not in contact list
  useEffect(() => {
    if (!client) return;
    
    const knownContactIds = new Set(contacts.map(c => c.id));
    const unknownSelectedIds = selectedContactIds.filter(id => !knownContactIds.has(id) && !externalUsers[id]);
    
    if (unknownSelectedIds.length === 0) return;
    
    let isMounted = true;
    
    const fetchExternalUsers = async () => {
      const newExternalUsers = {};
      
      for (const userId of unknownSelectedIds) {
        try {
          // Get profile info from Matrix
          const profile = await client.getProfileInfo(userId);
          const displayName = profile.displayname || userId;
          
          // Get avatar
          let avatarUrl = generateInitialsAvatar(displayName);
          if (profile.avatar_url) {
            try {
              avatarUrl = await getUserAvatar(userId, displayName, 40);
            } catch (err) {
              logger.warn(`Failed to fetch avatar for ${userId}`, err);
            }
          }
          
          newExternalUsers[userId] = {
            id: userId,
            name: displayName,
            avatar: avatarUrl,
          };
        } catch (error) {
          logger.error(`Failed to fetch profile for ${userId}:`, error);
          // Create minimal entry with initials
          newExternalUsers[userId] = {
            id: userId,
            name: userId,
            avatar: generateInitialsAvatar(userId),
          };
        }
      }
      
      if (isMounted && Object.keys(newExternalUsers).length > 0) {
        setExternalUsers(prev => ({ ...prev, ...newExternalUsers }));
      }
    };
    
    fetchExternalUsers();
    
    return () => {
      isMounted = false;
    };
  }, [client, selectedContactIds, contacts, externalUsers, getUserAvatar]);

  // Create a stable key from selected contact IDs for avatar computation dependency
  const selectedContactsKey = useMemo(() => {
    return [...selectedContactIds].sort().join(',');
  }, [selectedContactIds]);

  // Compute avatar URLs based on selected avatar option
  useEffect(() => {
    let isMounted = true;

    const computeAvatarUrls = async () => {
      logger.info('[useRoomEdit] Computing avatar URLs', { 
        existingRoomId, 
        selectedAvatarOption,
        hasClient: !!client,
      });

      if (!existingRoomId) {
        // For new rooms (shouldn't happen in useRoomEdit, but just in case)
        logger.warn('[useRoomEdit] No existingRoomId, setting avatarUrls to empty');
        setAvatarUrls([]);
        return;
      }

      try {
        // ALWAYS compute member avatars for modal's "Default" option display
        const currentSelectedContacts = contacts.filter(contact => 
          selectedContactIds.includes(contact.id),
        );
        
        // Build members array - ALWAYS include current user
        const members = [];
        
        // Add current user first
        if (currentUser && myUserId) {
          members.push({
            userId: myUserId,
            name: currentUser.displayName || myUserId,
            rawDisplayName: currentUser.rawDisplayName || currentUser.displayName || myUserId,
          });
        }
        
        // Add selected contacts
        currentSelectedContacts.forEach(contact => {
          members.push({
            userId: contact.id,
            name: contact.name,
            rawDisplayName: contact.name,
          });
        });
        
        if (members.length > 0) {
          const fallbacks = await getFallbackAvatarsFromMembers(members, 96);
          if (isMounted) {
            const memberUrls = fallbacks.map(desc => desc.url).filter(Boolean);
            logger.info('[useRoomEdit] Setting default avatar URLs (member avatars)', { 
              urlCount: memberUrls.length,
              totalMembers: members.length,
            });
            setDefaultAvatarUrls(memberUrls);
          }
        } else {
          if (isMounted) {
            logger.info('[useRoomEdit] No members, clearing default avatar URLs');
            setDefaultAvatarUrls([]);
          }
        }
        
        // NOW compute avatarUrls based on selected option
        if (selectedAvatarOption === 'default') {
          // For default, avatarUrls should be the same as defaultAvatarUrls (member avatars including current user)
          if (isMounted) {
            logger.info('[useRoomEdit] Setting avatarUrls to member avatars (default mode)');
            const fallbacks = await getFallbackAvatarsFromMembers(members, 96);
            const urls = fallbacks.map(desc => desc.url).filter(Boolean);
            setAvatarUrls(urls);
          }
        } else if (selectedAvatarOption === 'custom') {
          // Custom: Fetch current explicit room avatar
          logger.info('[useRoomEdit] Getting explicit room avatar');
          const roomName = room?.name || 'Room';
          const explicitAvatarUrl = await getRoomAvatar(existingRoomId, roomName, 96);
          
          if (isMounted) {
            if (explicitAvatarUrl) {
              logger.info('[useRoomEdit] Setting avatar URLs for custom (explicit room avatar)');
              setAvatarUrls([explicitAvatarUrl]);
            } else {
              // No explicit avatar - shouldn't happen, but fall back to member avatars
              logger.warn('[useRoomEdit] No explicit room avatar found, falling back to member avatars');
              const fallbacks = await getFallbackAvatars(existingRoomId, 96);
              const urls = fallbacks.map(desc => desc.url).filter(Boolean);
              setAvatarUrls(urls);
            }
          }
        } else if (selectedAvatarOption) {
          // Preset: Use the preset path directly
          if (isMounted) {
            logger.info('[useRoomEdit] Setting avatar URLs for preset', { path: selectedAvatarOption });
            setAvatarUrls([selectedAvatarOption]);
          }
        }
      } catch (error) {
        logger.error('Failed to fetch avatar URLs:', error);
        if (isMounted) {
          setAvatarUrls([]);
          setDefaultAvatarUrls([]);
        }
      }
    };

    computeAvatarUrls();

    return () => {
      isMounted = false;
    };
  }, [selectedAvatarOption, selectedContactsKey, existingRoomId, room, getRoomAvatar, getFallbackAvatars, getFallbackAvatarsFromMembers]);

  // Get available contacts (excluding already selected ones)
  const availableContacts = useMemo(() => {
    return contacts.filter(
      contact => 
        contact.id !== myUserId && 
        !selectedContactIds.includes(contact.id),
    );
  }, [contacts, myUserId, selectedContactIds]);

  // Get all current members in the room (including invited)
  const currentParticipants = useMemo(() => {
    if (client && existingRoomId) {
      const room = client.getRoom(existingRoomId);
      return room?.getMembers()
        .filter(m => m.membership === 'join' || m.membership === 'invite')
        .map(m => m.userId) || [];
    }
    return [];
  }, [client, existingRoomId]);

  // Get contacts that can be removed (for existing rooms)
  const removableContacts = useMemo(() => {
    if (!room) return selectedContacts;
    
    return selectedContacts.filter(contact => {
      // Can't remove current user unless explicitly allowed
      if (contact.id === myUserId && !allowRemoveCurrentUser) {
        return false;
      }
      // Can't remove if it would go below minimum participants
      const remainingCount = selectedContacts.length + 1; // +1 for current user
      return remainingCount > minParticipants;
    });
  }, [selectedContacts, myUserId, allowRemoveCurrentUser, minParticipants, room]);

  // Check if a contact is selected
  const isContactSelected = useCallback((contactId) => {
    return selectedContactIds.includes(contactId);
  }, [selectedContactIds]);

  // Check if a contact can be removed
  const canRemoveContact = useCallback((contactId) => {
    if (!room) return true;
    return removableContacts.some(contact => contact.id === contactId);
  }, [room, removableContacts]);

  // Toggle contact selection (add if not selected, remove if selected)
  // Behavior depends on maxSelections:
  // - maxSelections === 1: Auto-replace the selected contact
  // - maxSelections > 1: Lock selection (prevent adding more until one is removed)
  // - maxSelections === null: Unlimited selection
  const toggleContact = useCallback((contactId) => {
    setSelectedContactIds(prev => {
      if (prev.includes(contactId)) {
        // Check if we can remove this contact
        if (room && !canRemoveContact(contactId)) {
          return prev; // Don't remove if not allowed
        }
        // Remove contact
        return prev.filter(id => id !== contactId);
      } else {
        // Check if we've reached the max selection limit
        if (maxSelections !== null && prev.length >= maxSelections) {
          // Different behavior based on maxSelections value
          if (maxSelections === 1) {
            // Auto-replace: Replace the first (only) selected contact with the new one
            return [contactId];
          } else {
            // Lock down: Don't allow adding more until one is removed
            return prev;
          }
        }
        // Add contact normally
        return [...prev, contactId];
      }
    });
  }, [room, canRemoveContact, maxSelections]);

  // Add a contact to selection
  const addContact = useCallback((contactId) => {
    setSelectedContactIds(prev => {
      if (!prev.includes(contactId)) {
        return [...prev, contactId];
      }
      return prev;
    });
  }, []);

  // Remove a contact from selection
  const removeContact = useCallback((contactId) => {
    if (room && !canRemoveContact(contactId)) {
      return; // Don't remove if not allowed
    }

    setSelectedContactIds(prev => prev.filter(id => id !== contactId));
  }, [room, canRemoveContact]);

  // Add an external user (e.g., from VZD search) to the external users cache
  const addExternalUser = useCallback((user) => {
    if (user && user.id) {
      setExternalUsers(prev => ({
        ...prev,
        [user.id]: {
          id: user.id,
          name: user.name || user.id,
          avatar: user.avatar || generateInitialsAvatar(user.name || user.id),
        },
      }));
    }
  }, []);

  // Clear all selected contacts (respecting minimum requirements)
  const clearSelection = useCallback(() => {
    if (room && minParticipants > 1) {
      // For existing rooms, we might not be able to clear everything
      return;
    }
    setSelectedContactIds([]);
  }, [room, minParticipants]);

  // Set multiple contacts at once
  const setSelectedContactsById = useCallback((contacts) => {
    const contactIds = contacts.map(contact => contact.id);
    setSelectedContactIds(contactIds);
  }, []);

  // Get all participants for the conversation (current user + selected contacts)
  const allParticipants = useMemo(() => {
    const participants = [currentUser];
    if (selectedContacts.length > 0) {
      participants.push(...selectedContacts);
    }
    return participants.filter(Boolean); // Remove any null/undefined
  }, [currentUser, selectedContacts]);
  
  // Generate default room title based on participants
  const getDefaultTitle = useCallback(() => {
    if (room?.name) {
      return room.name;
    }
    
    if (selectedContacts.length === 0) {
      return currentUser?.displayName || 'New Conversation';
    }
    
    const contactNames = selectedContacts.map(contact => contact.name);
    return contactNames.join(', ');
  }, [room?.name, selectedContacts, currentUser?.displayName]);

  // Check if we can create a room (minimum participants)
  const canCreateRoom = useMemo(() => {
    const totalParticipants = selectedContacts.length + 1; // +1 for current user
    return totalParticipants >= minParticipants;
  }, [selectedContacts.length, minParticipants]);

  // Check if avatar has been changed from original
  const hasAvatarChanged = useMemo(() => {
    if (!room) return false; // New rooms don't have original avatar to change

    // Check if option type changed
    if (selectedAvatarOption !== originalAvatarOption) {
      return true;
    }

    // If still 'custom' but user selected a new file, that's also a change
    if (selectedAvatarOption === 'custom' && selectedAvatarFile) {
      return true;
    }

    return false;
  }, [room, selectedAvatarOption, originalAvatarOption, selectedAvatarFile]);

  // Check if current selection differs from original (for existing rooms)
  // This includes: participants, topic, and avatar changes
  const hasChanges = useMemo(() => {
    if (!room) return true; // New rooms always have "changes"
    if (!client) return false;
    
    // Check participant changes
    const originalParticipantIds = room?.getMembers()
      .filter(m => m.membership === 'join' || m.membership === 'invite')
      .map(m => m.userId)
      .filter(userId => userId !== myUserId)
      .sort() || [];
    
    const currentSelectionIds = [...selectedContactIds].sort();
    const participantsChanged = JSON.stringify(originalParticipantIds) !== JSON.stringify(currentSelectionIds);
    
    // Check topic changes
    const originalTopicEvent = room?.getLiveTimeline()
      .getState(EventTimeline.FORWARDS)
      .getStateEvents(GEMATIK_ROOM.ROOM_TOPIC, '');
    const originalTopic = (originalTopicEvent?.getContent()?.topic || '').trim();
    const currentTopic = (editedTopic || '').trim();
    const topicChanged = originalTopic !== currentTopic;
    
    // Avatar changes already tracked by hasAvatarChanged
    
    return participantsChanged || topicChanged || hasAvatarChanged;
  }, [client, room, myUserId, selectedContactIds, editedTopic, hasAvatarChanged]);

  // Get room data ready for creation/update
  const getRoomData = useCallback((customTitle = '') => {
    const title = customTitle.trim() || getDefaultTitle();
    return {
      title,
      participants: allParticipants,
      selectedContacts: selectedContacts,
      isUpdate: !!room,
      roomId: room?.id,
      hasChanges,
    };
  }, [getDefaultTitle, allParticipants, selectedContacts, room, hasChanges]);

  // Reset to original state (for existing rooms)
  const resetToOriginal = useCallback(() => {
    if (client && existingRoomId) {
      const room = client.getRoom(existingRoomId);
      const participantIds = room?.getMembers()
        .filter(m => m.membership === 'join' || m.membership === 'invite')
        .map(m => m.userId)
        .filter(userId => userId !== myUserId) || [];
      setSelectedContactIds(participantIds);
      
      // Reset topic
      const topicEvent = room?.getLiveTimeline()
        .getState(EventTimeline.FORWARDS)
        .getStateEvents(GEMATIK_ROOM.ROOM_TOPIC, '');
      const roomTopic = topicEvent?.getContent()?.topic || '';
      setEditedTopic(roomTopic);
      

      // Reset avatar to room's current state
      const roomAvatarMxc = room?.getMxcAvatarUrl();
      let resetOption;
      if (roomAvatarMxc) {
        // Room has explicit avatar - show as custom
        resetOption = 'custom';
        setSelectedAvatarOption('custom');
      } else {
        // No explicit avatar - use default
        resetOption = 'default';
        setSelectedAvatarOption('default');
      }
      setOriginalAvatarOption(resetOption); // Reset tracking
      setSelectedAvatarFile(null);
    } else {
      setSelectedContactIds([]);
      setEditedTopic('');
      setSelectedAvatarOption('default');
      setOriginalAvatarOption('default');
      setSelectedAvatarFile(null);
    }
  }, [client, existingRoomId, myUserId]);

  // Get users that will be invited (for pre-save validation)
  const getNewInvites = useCallback(() => {
    if (!client || !existingRoomId) return [];
    const room = client.getRoom(existingRoomId);
    if (!room) return [];

    const currentMemberIds = room.getMembers()
      .filter(m => m.membership === 'join' || m.membership === 'invite')
      .map(m => m.userId);

    const toInvite = selectedContactIds.filter(id => !currentMemberIds.includes(id));
    return toInvite;
  }, [client, existingRoomId, selectedContactIds]);

  // Unban a user 
  const unbanUser = useCallback(async (userId) => {
    if (!client || !existingRoomId) {
      throw new Error('No client or room ID');
    }

    try {
      await client.unban(existingRoomId, userId);
      logger.info('Unbanned user:', { userId, roomId: existingRoomId });
    } catch (error) {
      logger.error('Error unbanning user:', error);
      throw error;
    }
  }, [client, existingRoomId]);

  // Save room edits: update name and membership
  const saveRoomEdits = useCallback(async (editedTitle) => {
    if (!client || !existingRoomId) throw new Error('No client or room ID');
    const room = client.getRoom(existingRoomId);
    if (!room) throw new Error('Room not found');

    // 1. Update room name only if user explicitly changed it
    if (editedTitle != null) {
      const nameEvent = room.getLiveTimeline()
        .getState(EventTimeline.FORWARDS)
        .getStateEvents('m.room.name', '');
      const currentName = (nameEvent?.getContent()?.name || room.name || '').trim();
      await dualWriteRoomName(client, existingRoomId, editedTitle, currentName);
    }

    // 2. Update room topic if changed (dual-write: Gematik + standard)
    const topicEvent = room.getLiveTimeline()
      .getState(EventTimeline.FORWARDS)
      .getStateEvents('m.room.topic', '');
    const currentTopic = topicEvent?.getContent()?.topic || '';
    await dualWriteRoomTopic(client, existingRoomId, editedTopic, currentTopic);

    // 3. Update room membership
    const currentMemberIds = room.getMembers()
      .filter(m => m.membership === 'join' || m.membership === 'invite')
      .map(m => m.userId);

    const toInvite = selectedContactIds.filter(id => !currentMemberIds.includes(id));
    const toRemove = currentMemberIds.filter(id => !selectedContactIds.includes(id) && id !== client.getUserId());

    // Invite new members
    // NOTE: Following Element Web's behavior - we do NOT automatically remove
    // rooms from m.direct when adding 3rd+ members. This keeps DMs in the DM
    // section even with multiple members (Element Web Issue #16453).
    // Users must manually convert via slash commands if needed.

    // Check if this is a DM room to set is_direct flag on invites
    const isDMRoom = matrixService.isDMRoom(existingRoomId);

    for (const userId of toInvite) {
      try {
        // Always use client.invite() first - this uses the proper /invite endpoint
        // which notifies the invitee via their /sync response and handles federation
        await client.invite(existingRoomId, userId);

        // For DM rooms, update the member event to add is_direct flag
        // This ensures the invitee's client recognizes it as a DM invite
        // Must be done AFTER invite() since sendStateEvent alone doesn't notify the invitee
        if (isDMRoom) {
          await client.sendStateEvent(existingRoomId, 'm.room.member', {
            membership: 'invite',
            is_direct: true,
          }, userId);
        }
      } catch (error) {
        // Attach the failed userId so the UI can show which user couldn't be invited
        error.failedUserId = userId;
        throw error;
      }
    }

    // Remove members (kick)
    for (const userId of toRemove) {
      await client.kick(existingRoomId, userId, 'Removed by room edit');
    }

    return {
      invited: toInvite.length,
      removed: toRemove.length,
    };
  }, [client, existingRoomId, editedTopic, selectedContactIds]);

  // --- Permissions ---
  const powerLevels = useMemo(() => {
    if (!room) return null;

    const powerLevelsEvent = room
      .getLiveTimeline()
      .getState(EventTimeline.FORWARDS)
      .getStateEvents('m.room.power_levels', '');
    const content = powerLevelsEvent?.getContent() || null;

    return content;
  }, [room, myUserId, stateUpdateCounter]);

  const userPowerLevel = useMemo(() => {
    if (!powerLevels || !myUserId) return 0;
    return powerLevels.users?.[myUserId] ?? powerLevels.users_default ?? 0;
  }, [powerLevels, myUserId]);

  const canEditTopic = useMemo(() => {
    if (!room) return false;
    return canUserEditRoomTopic(powerLevels, userPowerLevel);
  }, [room, powerLevels, userPowerLevel]);

  const canInvite = useMemo(() => {
    if (!room || !powerLevels) {
      return false;
    }
    const inviteLevel = powerLevels.invite ?? 50;
    const result = userPowerLevel >= inviteLevel;
    return result;
  }, [room, powerLevels, userPowerLevel]);

  const canRename = useMemo(() => {
    if (!room) return false;
    return canUserEditRoomName(powerLevels, userPowerLevel);
  }, [room, powerLevels, userPowerLevel]);

  const canRemove = useMemo(() => {
    if (!room || !powerLevels) {
      return false;
    }
    const kickLevel = powerLevels.kick ?? 50;
    const result = userPowerLevel >= kickLevel;
    return result;
  }, [room, powerLevels, userPowerLevel]);

  const canSetRoomAvatar = useMemo(() => {
    if (!room || !powerLevels) {
      return false;
    }
    const avatarRequiredLevel = powerLevels.events?.['m.room.avatar'] ?? powerLevels.state_default ?? 50;
    const result = userPowerLevel >= avatarRequiredLevel;
    return result;
  }, [room, powerLevels, userPowerLevel]);

  // Avatar selection functions
  const selectDefaultAvatar = useCallback(() => {
    logger.info('[useRoomEdit] selectDefaultAvatar called');
    setSelectedAvatarOption('default');
    setSelectedAvatarFile(null);
  }, []);

  const selectPresetAvatar = useCallback((presetPath) => {
    logger.info('[useRoomEdit] selectPresetAvatar called', { presetPath });
    setSelectedAvatarOption(presetPath);
    setSelectedAvatarFile(null);
  }, []);

  const selectCustomAvatar = useCallback((file) => {
    logger.info('[useRoomEdit] selectCustomAvatar called', { fileName: file?.name });
    setSelectedAvatarOption('custom');
    setSelectedAvatarFile(file);
  }, []);

  const isBanned = useCallback((userId) => {
    if (!existingRoomId) return false;
    const room = client?.getRoom(existingRoomId);
    if (!room) return false;
    const member = room.getMember(userId);
    return member?.membership === 'ban';
  }, [client, existingRoomId]);

  return {
    // Selection state
    selectedContactIds,
    selectedContacts,
    availableContacts,
    allParticipants,
    currentParticipants,
    removableContacts,
    externalUsers: Object.values(externalUsers), // Cached users not in contact list
    
    // Selection actions
    isContactSelected,
    canRemoveContact,
    toggleContact,
    addContact,
    removeContact,
    addExternalUser,
    clearSelection,
    setSelectedContactsById,
    resetToOriginal,
    
    // Room helpers
    saveRoomEdits,
    getNewInvites,
    isBanned,
    unbanUser,
    getDefaultTitle,
    canCreateRoom,
    getRoomData,
    hasChanges,
    
    // Computed values
    selectedCount: selectedContacts.length,
    hasSelection: selectedContacts.length > 0,
    isEditMode: !!room,
    minParticipants,
    maxSelections,

    // Room Edit Permissions
    canInvite,
    canRename,
    canRemove,
    canSetRoomAvatar,
    canEditTopic, 
    
    // Topic state
    editedTopic,
    setEditedTopic,

    // Avatar state
    selectedAvatarOption,
    selectedAvatarFile,
    avatarUrls,
    defaultAvatarUrls, // Member avatars for modal's "Default" option
    hasAvatarChanged, // Whether avatar has been modified from original
    
    // Avatar selection functions
    selectDefaultAvatar,
    selectPresetAvatar,
    selectCustomAvatar,
  };
};

export default useRoomEdit;
